/**
 * Admin logs API queries
 *
 * Gateway mapping:
 *   /flow-executions/enriched  → gateway aggregation over flow-observer (ADMIN / DEVELOPER for tenant data)
 *   /flow-observer/api/v1/...  → flow-observer (execution step events)
 *   /action-logs/api/v1/...    → action-logs audit trail (ADMIN only)
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/_shared/http';
import { RETRY_ONCE, STALE_1M } from '@/api/_shared/queryDefaults';
import { logsKeys } from './keys';
import {
  FLOW_EXECUTION_STATUSES,
  type ActionLogEntry,
  type ActionLogFilters,
  type EnrichedFlowExecution,
  type FlowExecutionEvent,
  type FlowExecutionFilters,
  type FlowExecutionStatus,
  type PaginatedLogsResponse
} from './interfaces';

/**
 * Serialises filters into a query string, skipping empty values.
 * @param params - Flat filter object.
 */
function toQueryString(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  }
  return query.toString();
}

export const getFlowExecutionsQueryFn = (filters: FlowExecutionFilters) =>
  apiGet<PaginatedLogsResponse<EnrichedFlowExecution>>(
    `/flow-executions/enriched?${toQueryString({ ...filters, sortBy: 'createdAt', sortOrder: filters.sortOrder ?? 'desc' })}`
  );

export const getFlowExecutionEventsQueryFn = (executionId: string) =>
  apiGet<FlowExecutionEvent[]>(`/flow-observer/api/v1/executions/${encodeURIComponent(executionId)}/events`);

export const getActionLogsQueryFn = (filters: ActionLogFilters) =>
  apiGet<PaginatedLogsResponse<ActionLogEntry>>(
    `/action-logs/api/v1/action-logs?${toQueryString({ ...filters, sortBy: 'timestamp', sortOrder: 'desc' })}`
  );

/**
 * Paginated flow executions across all tenants, or one tenant when `clientId` is set.
 * @param filters - Pagination and filters; changing them keeps the previous page visible while loading.
 * @param refetchInterval - Optional auto-refresh interval in ms.
 */
export const useFlowExecutionsQuery = (filters: FlowExecutionFilters, refetchInterval: number | false = false) =>
  useQuery({
    queryKey: logsKeys.flowExecutions(filters),
    queryFn: () => getFlowExecutionsQueryFn(filters),
    placeholderData: keepPreviousData,
    staleTime: STALE_1M,
    retry: RETRY_ONCE,
    refetchInterval
  });

/**
 * Execution counts per status for the KPI cards, derived from the `total` of one-row requests
 * (the backend has no stats endpoint).
 * @param filters - Same scope as the table, without status and pagination.
 * @param refetchInterval - Optional auto-refresh interval in ms.
 */
export const useFlowExecutionStatusCountsQuery = (
  filters: Omit<FlowExecutionFilters, 'page' | 'limit' | 'status'>,
  refetchInterval: number | false = false
) =>
  useQuery({
    queryKey: logsKeys.flowExecutionCounts(filters),
    queryFn: async () => {
      const totals = await Promise.all(
        FLOW_EXECUTION_STATUSES.map((status) => getFlowExecutionsQueryFn({ ...filters, status, page: 1, limit: 1 }))
      );
      const counts = Object.fromEntries(FLOW_EXECUTION_STATUSES.map((status, index) => [status, totals[index].total])) as Record<
        FlowExecutionStatus,
        number
      >;
      return { ...counts, total: totals.reduce((sum, response) => sum + response.total, 0) };
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_1M,
    retry: RETRY_ONCE,
    refetchInterval
  });

/**
 * Step events of one execution, for the detail drawer.
 * @param executionId - Execution to load; the query is idle while it is undefined.
 */
export const useFlowExecutionEventsQuery = (executionId: string | undefined) =>
  useQuery({
    queryKey: logsKeys.flowExecutionEvents(executionId ?? ''),
    queryFn: () => getFlowExecutionEventsQueryFn(executionId as string),
    enabled: !!executionId,
    staleTime: STALE_1M,
    retry: RETRY_ONCE
  });

/**
 * Paginated audit trail (flow and template changes). ADMIN only on the backend.
 * @param filters - Pagination and filters.
 * @param enabled - Set false for users without access to avoid a 403.
 */
export const useActionLogsQuery = (filters: ActionLogFilters, enabled = true) =>
  useQuery({
    queryKey: logsKeys.actionLogs(filters),
    queryFn: () => getActionLogsQueryFn(filters),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: STALE_1M,
    retry: RETRY_ONCE
  });
